import {
  Carousel,
  CarouselContent,
  CarouselPrevious,
  CarouselNext,
} from '@synoptic/ui-kit/carousel/carousel.js';
import { NavArrowRightIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-right.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { Skeleton } from '@synoptic/ui-kit/skeleton/skeleton.js';
import clsx from 'clsx';
import { PropsWithChildren } from 'react';
import { Link } from '@/routes-utils/navigation';
import {
  carouselLeftButton,
  carouselButton,
  sectionLabel,
  sectionHeader,
  sectionNavigation,
  carouselItemFullWidth,
  carouselItemHalfWidth,
  carouselItemQuarterWidth,
  carouselItemThirdWidth,
  sectionHeaderContent,
  sectionShowAllLink,
} from './home-sections.css';

type SkeletonVariant = 'full' | 'half' | 'quarter' | 'third';

const skeletonCount: Record<SkeletonVariant, number> = {
  full: 1,
  half: 2,
  quarter: 4,
  third: 3,
};

export const HomeCarousel = ({
  children,
  title,
  skeleton,
  showAllUrl,
}: PropsWithChildren<{
  title?: string;
  skeleton?: {
    height: number;
    variant: SkeletonVariant;
    show: boolean;
  };
  showAllUrl?: string;
}>) => {
  const isTablet = useIsTablet();

  return (
    <Carousel opts={{ slidesToScroll: 'auto' }}>
      {title && (
        <HomeSectionHeader showAllUrl={showAllUrl} title={title}>
          {isTablet && (
            <div className={sectionNavigation}>
              <CarouselPrevious
                variant="ghost"
                size="large"
                className={clsx(carouselButton, carouselLeftButton)}
              >
                <NavArrowRightIcon />
              </CarouselPrevious>
              <CarouselNext
                variant="ghost"
                size="large"
                className={carouselButton}
              >
                <NavArrowRightIcon />
              </CarouselNext>
            </div>
          )}
        </HomeSectionHeader>
      )}

      <CarouselContent>
        {skeleton?.show ? (
          <>
            {Array.from({ length: skeletonCount[skeleton.variant] }).map(
              (_, index) => (
                <Skeleton
                  key={index}
                  className={clsx({
                    [carouselItemFullWidth]: skeleton.variant === 'full',
                    [carouselItemHalfWidth]: skeleton.variant === 'half',
                    [carouselItemQuarterWidth]: skeleton.variant === 'quarter',
                    [carouselItemThirdWidth]: skeleton.variant === 'third',
                  })}
                  height={skeleton.height}
                />
              ),
            )}
          </>
        ) : (
          children
        )}
      </CarouselContent>
    </Carousel>
  );
};

export const HomeSectionHeader = ({
  title,
  children,
  className,
  showAllUrl,
}: PropsWithChildren<{
  title: string;
  className?: string;
  showAllUrl?: string;
}>) => {
  return (
    <div className={clsx(sectionHeader, className)}>
      <div className={sectionHeaderContent}>
        <h2 className={sectionLabel}>{title}</h2>
        {showAllUrl && (
          <Link className={sectionShowAllLink} to={showAllUrl}>
            See all
          </Link>
        )}
      </div>
      {children}
    </div>
  );
};
